import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Interfaces
export interface BomMaster {
  bom_id: number;
  project_id: number;
  segment_id?: number;
  wbs_id?: number;
  bom_code: string;
  description?: string;
  created_by: number;
  creation_date: string;
  version: number;
  status: 'draft' | 'pending' | 'approved' | 'rejected' | 'deleted';
  approval_date?: string;
  approved_by?: number;
  metadata?: any;
  updated_at: string;
  project_name?: string;
  wbs_name?: string;
  creator_first_name?: string;
  creator_last_name?: string;
  approver_first_name?: string;
  approver_last_name?: string;
  material_count?: number;
  total_amount?: number;
}

export interface BomMaterial {
  bom_material_id?: number;
  bom_id: number;
  material_id: number;
  quantity: number;
  unit_rate: number;
  total_amount: number;
  remarks?: string;
  metadata?: any;
  material_code?: string;
  material_name?: string;
  material_description?: string;
  unit?: string;
  category?: string;
  specifications?: any;
}

export interface BomTemplate {
  template_id: number;
  template_name: string;
  template_code: string;
  description?: string;
  category?: string;
  is_public: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  materials?: BomMaterial[];
}

export interface BomStats {
  total_boms: number;
  draft_boms: number;
  pending_boms: number;
  approved_boms: number;
  rejected_boms: number;
  new_this_month: number;
  approved_this_week: number;
  overdue_pending?: number;
  avg_approval_days?: number;
}

export interface BomFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  project_id?: number;
  segment_id?: number;
  wbs_id?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface PaginatedResponse<T> {
  status: string;
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface ApiResponse<T> {
  status: string;
  message?: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class BomService {
  private baseUrl = `${environment.BASE_URL}boms`;

  constructor(private http: HttpClient) {}

  // BOM Master operations
  getBoms(filters: BomFilters = {}): Observable<PaginatedResponse<BomMaster>> {
    let params = new HttpParams();
    
    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof BomFilters];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<PaginatedResponse<BomMaster>>(`${this.baseUrl}`, { params });
  }

  getBom(bomId: number): Observable<ApiResponse<BomMaster & { materials: BomMaterial[] }>> {
    return this.http.get<ApiResponse<BomMaster & { materials: BomMaterial[] }>>(`${this.baseUrl}/${bomId}`);
  }

  createBom(bomData: Partial<BomMaster> & { materials?: BomMaterial[] }): Observable<ApiResponse<BomMaster>> {
    return this.http.post<ApiResponse<BomMaster>>(`${this.baseUrl}`, bomData);
  }

  updateBom(bomId: number, bomData: Partial<BomMaster> & { materials?: BomMaterial[] }): Observable<ApiResponse<BomMaster>> {
    return this.http.put<ApiResponse<BomMaster>>(`${this.baseUrl}/${bomId}`, bomData);
  }

  deleteBom(bomId: number): Observable<ApiResponse<BomMaster>> {
    return this.http.delete<ApiResponse<BomMaster>>(`${this.baseUrl}/${bomId}`);
  }

  // BOM Materials operations
  getBomMaterials(bomId: number): Observable<ApiResponse<BomMaterial[]>> {
    return this.http.get<ApiResponse<BomMaterial[]>>(`${this.baseUrl}/${bomId}/materials`);
  }

  addBomMaterial(bomId: number, material: Partial<BomMaterial>): Observable<ApiResponse<BomMaterial>> {
    return this.http.post<ApiResponse<BomMaterial>>(`${this.baseUrl}/${bomId}/materials`, material);
  }

  updateBomMaterial(materialId: number, material: Partial<BomMaterial>): Observable<ApiResponse<BomMaterial>> {
    return this.http.put<ApiResponse<BomMaterial>>(`${this.baseUrl}/materials/${materialId}`, material);
  }

  deleteBomMaterial(materialId: number): Observable<ApiResponse<BomMaterial>> {
    return this.http.delete<ApiResponse<BomMaterial>>(`${this.baseUrl}/materials/${materialId}`);
  }

  // BOM Approval operations
  approveBom(bomId: number, approvalData: {
    approver_id: number;
    approval_level?: number;
    comments?: string;
  }): Observable<ApiResponse<BomMaster>> {
    return this.http.post<ApiResponse<BomMaster>>(`${this.baseUrl}/${bomId}/approve`, approvalData);
  }

  rejectBom(bomId: number, rejectionData: {
    rejected_by: number;
    comments?: string;
  }): Observable<ApiResponse<BomMaster>> {
    return this.http.post<ApiResponse<BomMaster>>(`${this.baseUrl}/${bomId}/reject`, rejectionData);
  }

  // BOM Statistics
  getBomStats(): Observable<ApiResponse<BomStats>> {
    return this.http.get<ApiResponse<BomStats>>(`${this.baseUrl}/stats`);
  }

  // BOM Export
  exportBom(bomId: number, format: 'json' | 'csv' = 'json'): Observable<any> {
    const params = new HttpParams().set('format', format);
    const options: any = { params };
    
    if (format === 'csv') {
      options.responseType = 'text';
    }
    
    return this.http.get(`${this.baseUrl}/${bomId}/export`, options);
  }

  // BOM Templates operations
  getBomTemplates(): Observable<BomTemplate[]> {
    return this.http.get<BomTemplate[]>(`${this.baseUrl}/templates`);
  }

  getBomTemplate(templateId: number): Observable<BomTemplate> {
    return this.http.get<BomTemplate>(`${this.baseUrl}/templates/${templateId}`);
  }

  createBomTemplate(template: Partial<BomTemplate>): Observable<BomTemplate> {
    return this.http.post<BomTemplate>(`${this.baseUrl}/templates`, template);
  }

  updateBomTemplate(templateId: number, template: Partial<BomTemplate>): Observable<BomTemplate> {
    return this.http.put<BomTemplate>(`${this.baseUrl}/templates/${templateId}`, template);
  }

  deleteBomTemplate(templateId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/templates/${templateId}`);
  }

  // Utility methods
  calculateMaterialTotal(material: BomMaterial): number {
    const quantity = material.quantity || 0;
    const unitRate = material.unit_rate || 0;
    return quantity * unitRate;
  }

  calculateBomTotals(materials: BomMaterial[]): { subtotal: number; total: number } {
    const subtotal = materials.reduce((sum, material) => {
      return sum + this.calculateMaterialTotal(material);
    }, 0);

    return {
      subtotal: Number(subtotal.toFixed(2)),
      total: Number(subtotal.toFixed(2))
    };
  }

  generateBomCode(projectId: number, existingCount: number = 0): string {
    const count = existingCount + 1;
    return `BOM-${projectId}-${count.toString().padStart(3, '0')}`;
  }

  // Status utilities
  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'draft': return '#9e9e9e';
      case 'pending': return '#ff9800';
      case 'approved': return '#4caf50';
      case 'rejected': return '#f44336';
      case 'deleted': return '#757575';
      default: return '#9e9e9e';
    }
  }

  getStatusIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'draft': return 'edit';
      case 'pending': return 'schedule';
      case 'approved': return 'check_circle';
      case 'rejected': return 'cancel';
      case 'deleted': return 'delete';
      default: return 'help';
    }
  }

  getStatusClass(status: string): string {
    return `status-${status.toLowerCase()}`;
  }

  // Format utilities
  formatCurrency(amount: number, currency: string = 'INR'): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(date: string): string {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Validation utilities
  validateBomData(bom: Partial<BomMaster>): string[] {
    const errors: string[] = [];

    if (!bom.project_id) {
      errors.push('Project is required');
    }

    if (!bom.bom_code) {
      errors.push('BOM code is required');
    }

    if (bom.description && bom.description.length > 1000) {
      errors.push('Description must be less than 1000 characters');
    }

    return errors;
  }

  validateBomMaterial(material: Partial<BomMaterial>): string[] {
    const errors: string[] = [];

    if (!material.material_id) {
      errors.push('Material is required');
    }

    if (!material.quantity || material.quantity <= 0) {
      errors.push('Quantity must be greater than 0');
    }

    if (!material.unit_rate || material.unit_rate < 0) {
      errors.push('Unit rate must be 0 or greater');
    }

    if (material.remarks && material.remarks.length > 500) {
      errors.push('Remarks must be less than 500 characters');
    }

    return errors;
  }
}
